<template>
  <div class="AggregateStatusPopoverDetailed">
    <div style="display: flex; align-items: center; gap: 5px;">
      <h3 style="margin-right: 10px;">
        {{ popoverTitle }}
      </h3>
      <template v-if="showingDesktopUi">
        <span v-if="store.showAdvanced && tag.sensors.children.length == 1" style="font-family: 'Reddit Mono'; padding-top: 1px; margin-right: 10px;">
          {{ channelLabel }}
        </span>
        <AggregateStatusAuxiliariesOrder :tag="tag"></AggregateStatusAuxiliariesOrder>
        <Button v-if="showControlTempPriority" text severity="secondary" size="small" @click="onClickPriority">
          Control priority
          <i class="pi pi-angle-down"></i>
        </Button>
        <TieredMenu ref="priorityMenu" popup :model="priorityMenuItems"></TieredMenu>
        <Button v-if="showingCalibrateButton" :disabled="isCalibrateButtonDisabled" text severity="secondary" size="small" @click="onClickCalibrate">
          Calibrate
        </Button>
        <TieredMenu ref="calibrateMenu" popup :model="calibrateMenuItems"></TieredMenu>
        <Button text severity="secondary" size="small" @click="openHistory(tag)">
          View history
        </Button>
      </template>
      <div style="flex-grow: 1;"></div>
      <Button text severity="secondary" style="margin-left: 10px; margin-right: -10px; color: gray; border: 0;" @click="emit('update:showingPopover', false)">
        <i class="pi pi-times"></i>
      </Button>
    </div>
    <template v-if="!showingDesktopUi">
      <div v-if="store.showAdvanced && tag.sensors.children.length == 1" style="text-align: center; font-family: 'Consolas';">
        {{ channelLabel }}
      </div>
      <div style="display: flex; flex-direction: column; align-items: stretch; justify-content: center; gap: 5px;">
        <AggregateStatusAuxiliariesOrder :tag="tag"></AggregateStatusAuxiliariesOrder>
        <Button v-if="showControlTempPriority" text severity="secondary" size="small" @click="onClickPriority">
          Control priority
          <i class="pi pi-angle-down"></i>
        </Button>
        <TieredMenu ref="priorityMenu" popup :model="priorityMenuItems"></TieredMenu>
        <Button v-if="showingCalibrateButton" :disabled="isCalibrateButtonDisabled" text severity="secondary" size="small" @click="onClickCalibrate">
          Calibrate
        </Button>
        <TieredMenu ref="calibrateMenu" popup :model="calibrateMenuItems"></TieredMenu>
        <Button text severity="secondary" size="small" @click="openHistory(tag)">
          View history
        </Button>
      </div>
    </template>
    <div class="popoverContent">
      <SingleColumnTable v-if="sensorsTable" style="border: 0;"
        :style="{ marginTop: showingMultiColumnSettings ? '39px' : 0 }"
        :session="tag.$session"
        :tableData="sensorsTable"
        :column="columnIndex"
        :selectedRowId="selectedSensorsTableRowId"
        @update:selectedRowId="id => setSelectedSensorsTableRowId(id, false)"
      >
        <template #header="{row}">
          <template v-if="row.cells[0].componentType == 'SensorStatus'">
            <span style="display: flex; align-items: center; gap: 10px;">
              {{ row.cells[0].componentArgs.tag.name.value }}
              <span v-if="store.showAdvanced" style="font-family: Consolas; font-size: 14px;">
                ({{ controlChannelLabel(row.cells[0].componentArgs.tag) }})
              </span>
            </span>
          </template>
        </template>
      </SingleColumnTable>
      <SingleColumnTable v-if="isControlTemp"
        :session="tag.$session"
        :tableData="controlTempStatusTableData"
        :column="0"
      ></SingleColumnTable>
      <SingleColumnTable v-if="showingSingleColumnSettings" style="border: 0;"
        :session="tag.$session"
        :tableData="settingsTable"
      ></SingleColumnTable>
      <MultiColumnTable v-else-if="showingMultiColumnSettings"
        :tableName="''"
        :tableData="settingsTable"
        :disableFirstRenderDelay="true"
      ></MultiColumnTable>
    </div>
  </div>
</template>

<script setup>
import store, { showingDesktopUi } from "./store.js";
import SingleColumnTable from "./SingleColumnTable.vue";
import MultiColumnTable from "./MultiColumnTable.vue";
import AggregateStatusAuxiliariesOrder from "./AggregateStatusAuxiliariesOrder.vue";
import { computed, ref } from "vue";
import { createSingleColumnTableData, sortRows } from "./TableUtility";
import { controlChannelLabel, openHistory, useBin } from "./appUtility.js";
import { sortBy } from "./utility.js";

let props = defineProps([
  "tag",
  "columnTag",
  "popoverTitle",
  "settingsTable",
  "controlTempAggrs",
  "showControlTempPriority",
]);

let emit = defineEmits([
  "calibrate",
  "update:showingPopover"
]);

let bin = computed(() => {
  let system = props.tag.$session.system;
  return system.bins.children.find(bin => bin == props.columnTag) ?? system;
});

let columnIndex = computed(() => {
  return bin.value?.$key() ?? 0;
});

let channelLabel = computed(() => {
  let sensors = props.tag.sensors.children;
  if (sensors.length == 0) return "(computed)";
  else if (sensors.length == 1) return controlChannelLabel(sensors[0]);
  else return null;
});

let calibrateMenu = ref(null);
let calibrateMenuItems = computed(() => {
  let sensors = props.tag.sensors.children;
  if (sensors.length <= 1) return null;
  return sensors.map((sensor, index) => {
    let row = sensorsTable.value.rows[index];
    return {
      label: sensor.name.value,
      disabled: sensor.commError.value,
      command: () => setSelectedSensorsTableRowId(row.id, true)
    };
  });
});

let showingCalibrateButton = computed(() => {
  let showingAdvanced = store.showAdvanced;
  let isComputedAggregate = props.tag.sensors.children.length == 0;
  return (showingAdvanced && !isComputedAggregate);
});

let isCalibrateButtonDisabled = computed(() => {
  let sensors = props.tag.sensors.children;
  return sensors.every(sensor => sensor.commError.value);
});

function onClickCalibrate(event) {
  let sensors = props.tag.sensors.children;
  if (sensors.length == 1)
    emit("calibrate");
  else
    calibrateMenu.value.toggle(event);
}

let priorityMenu = ref();
function onClickPriority(event) {
  priorityMenu.value.toggle(event);
}

let priorityMenuItems = computed(() => {
  return props.controlTempAggrs.map((aggr, index) => ({
    label: `Priority ${index + 1}`,
    command: () => setAggregatePriority(index)
  }));
});

function setAggregatePriority(newPriority) {
  let tag1 = props.tag;
  let tag2 = props.controlTempAggrs[newPriority];
  props.tag.$session.changeSettings([
    { handle: tag1.controlTempPriority.handle, value: newPriority },
    { handle: tag2.controlTempPriority.handle, value: tag1.controlTempPriority.value }
  ]);
  emit("update:showingPopover", false);
}

let sensorsTable = computed(() => {
  let sensors = props.tag.sensors.children;
  if (sensors.length <= 1 && !props.tag.rangeError.value)
    return null;

  let sensorsTable = {
    tableName: "",
    columns: [{ tag: bin.value, name: "", subColumns: [] }],
    rows: [
      ... sensors.map(sensor => ({
        id: sensor.name.value,
        rowName: sensor.name.value,
        sensor,
        cells: [{
          componentType: "SensorStatus",
          componentArgs: {
            tag: sensor,
            showingCalibrationKeypad: showingSensorCalibrationKeypad.value,
            "onUpdate:showingCalibrationKeypad": (value) => showingSensorCalibrationKeypad.value = value
          }
        }]
      })),
    ]
  };

  if (sensors.length > 1) {
    sensorsTable.rows.push({
      id: "method",
      rowName: "Method",
      cells: [{
        componentType: "ListSetting",
        componentArgs: {
          tag: props.tag.method,
          popoverTitle: "Method",
          options: [
            { caption: 'Average', value: 'avg' },
            { caption: 'Minimum', value: 'min' },
            { caption: 'Maximum', value: 'max' },
            { caption: 'Priority', value: 'prio' }
          ]
        }
      }]
    });
  }

  return sensorsTable;
});

let isControlTemp = computed(() => {
  return props.tag == bin.value?.controlTemp;
});

let controlTempAggrs = computed(() => {
  let temperatureAggrs = [
    bin.value.supply.temperature,
    bin.value.returnAir.temperature,
    ... bin.value.auxiliaries.children.map(aux => aux.temperature)
  ];
  let controlTempAggrs = temperatureAggrs.filter(aggr => aggr.controlTempToggle.value);
  let controlTempAggrsByPriority = sortBy(controlTempAggrs, aggr => aggr.controlTempPriority.value);
  return controlTempAggrsByPriority;
});

let controlTempStatusTableData = computed(() => {
  let system = props.tag.$session.system;
  let tableData = system.tables.controlTempStatus.value;
  let columnIndex = bin.value.$key();
  let singleColumnTableData = createSingleColumnTableData(tableData, columnIndex);

  // Sort the rows either by priority or by auxiliary order.
  let sortOrder = (bin.value.controlTemp.method.value == "prio" 
    ? controlTempAggrs.value.map(aggr => aggr.$parent.name.value)
    : system.auxiliariesOrder.value
  );
  singleColumnTableData.rows = sortRows(singleColumnTableData.rows, sortOrder);
  return singleColumnTableData;
});

let selectedSensorsTableRowId = ref(null);
let showingSensorCalibrationKeypad = ref(false);
function setSelectedSensorsTableRowId(id, showCalibrationKeypad) {
  selectedSensorsTableRowId.value = id;
  showingSensorCalibrationKeypad.value = showCalibrationKeypad;
}

let showingSingleColumnSettings = computed(() => {
  return props.settingsTable?.columns.length == 1;
});

let showingMultiColumnSettings = computed(() => {
  return props.settingsTable?.columns.length > 1;
});
</script>

<style scoped>
.AggregateStatusPopoverDetailed {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.popoverContent {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 50px;
}
.mobile .popoverContent {
  flex-direction: column-reverse;
  gap: 0px;
}
.mobile .SingleColumnTable {
  align-self: stretch;
}
</style>

<style>
.AggregateStatusPopoverDetailed .cell {
  padding-left: 0;
  padding-right: 0;
}
.AggregateStatusPopoverDetailed .MultiColumnTable {
  --border-style: 0;
  --table-border-bottom-style: 0;
}
.AggregateStatusPopoverDetailed .MultiColumnTable th {
  background: transparent;
  font-weight: normal;
}
.AggregateStatusPopoverDetailed .MultiColumnTable thead th {
  height: 39px;
}
.AggregateStatusPopoverDetailed .MultiColumnTable tbody th {
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 0;
  text-align: left;
  font-weight: normal;
  color: #334155;
}
</style>